// useLiveVoice — Gemini 3.1 Flash Live Preview bidirectional session hook.
//
// Responsibilities:
//   - Obtain microphone access and feed a 16 kHz / s16le / base64 stream of
//     audio frames to the worker via WebSocket.
//   - Receive 24 kHz / s16le audio frames from the model and schedule them
//     back-to-back through Web Audio so playback feels continuous.
//   - Surface a compact state machine for UI wiring.
//
// We deliberately do NOT share state with the existing TTS `useAudioPlayer`
// hook — Live playback needs raw PCM chunks, not an <audio> element.

import { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../firebase';

const API_BASE = import.meta.env.VITE_API_BASE as string;

// Gemini Live input/output rates (from the Live API docs).
const INPUT_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;

export type LiveVoiceState =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'listening'
  | 'speaking'
  | 'error';

export interface StartLiveOptions {
  conversationId?: string | null;
}

export interface UseLiveVoiceResult {
  state: LiveVoiceState;
  start: (opts?: StartLiveOptions) => Promise<void>;
  stop: () => void;
  sendText: (text: string) => void;
  transcript: string;
  outputTranscript: string;
  error: string | null;
  toolActive: boolean;
}

interface ServerAudioMessage {
  type: 'audio';
  b64: string;
}
interface ServerTextMessage {
  type: 'text' | 'input_transcript' | 'output_transcript';
  text: string;
}
interface ServerToolCallMessage {
  type: 'tool_call';
  calls: Array<{ name: string; args: Record<string, unknown> }>;
}
interface ServerTurnCompleteMessage {
  type: 'turn_complete';
}
interface ServerErrorMessage {
  type: 'error';
  msg: string;
}

type ServerMessage =
  | ServerAudioMessage
  | ServerTextMessage
  | ServerToolCallMessage
  | ServerTurnCompleteMessage
  | ServerErrorMessage;

export function useLiveVoice(): UseLiveVoiceResult {
  const [state, setState] = useState<LiveVoiceState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  const [outputTranscript, setOutputTranscript] = useState('');
  const [toolActive, setToolActive] = useState(false);

  // Persistent refs — these do not trigger re-renders.
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureCtxRef = useRef<AudioContext | null>(null);
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  // Scheduled start time for the next queued playback chunk.
  const playHeadRef = useRef<number>(0);
  // Track whether we are currently audibly playing anything back.
  const speakingTimerRef = useRef<number | null>(null);

  const cleanup = useCallback((): void => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    }
    if (workletNodeRef.current) {
      try {
        workletNodeRef.current.disconnect();
      } catch {
        /* ignore */
      }
      workletNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
      } catch {
        /* ignore */
      }
      sourceNodeRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    if (captureCtxRef.current) {
      try {
        void captureCtxRef.current.close();
      } catch {
        /* ignore */
      }
      captureCtxRef.current = null;
    }
    if (playbackCtxRef.current) {
      try {
        void playbackCtxRef.current.close();
      } catch {
        /* ignore */
      }
      playbackCtxRef.current = null;
    }
    if (speakingTimerRef.current !== null) {
      window.clearTimeout(speakingTimerRef.current);
      speakingTimerRef.current = null;
    }
    playHeadRef.current = 0;
  }, []);

  const stop = useCallback((): void => {
    cleanup();
    setState('idle');
    setToolActive(false);
  }, [cleanup]);

  // Schedule a PCM chunk for back-to-back playback.
  const scheduleAudio = useCallback((b64: string): void => {
    const ctx = playbackCtxRef.current;
    if (!ctx) return;
    const bytes = base64ToUint8Array(b64);
    if (bytes.length < 2) return;
    const samples = bytes.length / 2;
    const buffer = ctx.createBuffer(1, samples, OUTPUT_SAMPLE_RATE);
    const data = buffer.getChannelData(0);
    const view = new DataView(
      bytes.buffer,
      bytes.byteOffset,
      bytes.byteLength,
    );
    for (let i = 0; i < samples; i++) {
      const s16 = view.getInt16(i * 2, true);
      data[i] = s16 / 0x8000;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    const now = ctx.currentTime;
    const startAt = Math.max(playHeadRef.current, now + 0.02);
    source.start(startAt);
    playHeadRef.current = startAt + buffer.duration;

    // Flip to 'speaking' for the duration of this chunk.
    setState((s) => (s === 'error' || s === 'idle' ? s : 'speaking'));
    if (speakingTimerRef.current !== null) {
      window.clearTimeout(speakingTimerRef.current);
    }
    const remainingMs = Math.max(
      50,
      (playHeadRef.current - ctx.currentTime) * 1000,
    );
    speakingTimerRef.current = window.setTimeout(() => {
      setState((s) => (s === 'speaking' ? 'listening' : s));
      speakingTimerRef.current = null;
    }, remainingMs + 40);
  }, []);

  const start = useCallback(async (opts?: StartLiveOptions): Promise<void> => {
    if (state !== 'idle' && state !== 'error') return;
    setError(null);
    setTranscript('');
    setOutputTranscript('');
    setToolActive(false);

    if (typeof WebSocket === 'undefined') {
      setError('WebSocket is not supported in this browser.');
      setState('error');
      return;
    }
    if (
      typeof AudioWorkletNode === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setError(
        'This browser does not support AudioWorklet or microphone capture.',
      );
      setState('error');
      return;
    }

    setState('connecting');

    // 1. Auth token (fresh)
    let token: string;
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      token = await user.getIdToken();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setState('error');
      return;
    }

    // 2. Mic access
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      setError(
        `Microphone permission denied: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
      setState('error');
      return;
    }
    streamRef.current = stream;

    // 3. Capture + playback audio contexts. Capture context stays at the
    // hardware default (usually 48 kHz); we downsample manually to 16 kHz.
    const CtxCtor: typeof AudioContext =
      (window as unknown as { AudioContext?: typeof AudioContext })
        .AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext!;
    const captureCtx = new CtxCtor();
    const playbackCtx = new CtxCtor({ sampleRate: OUTPUT_SAMPLE_RATE });
    captureCtxRef.current = captureCtx;
    playbackCtxRef.current = playbackCtx;

    try {
      await captureCtx.audioWorklet.addModule(
        '/audio-worklet/pcm-capture.js',
      );
    } catch (err) {
      setError(
        `Failed to load audio worklet: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
      setState('error');
      cleanup();
      return;
    }

    const source = captureCtx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(captureCtx, 'pcm-capture');
    sourceNodeRef.current = source;
    workletNodeRef.current = node;

    // 4. WebSocket
    const qs = new URLSearchParams({ token });
    if (opts?.conversationId) qs.set('conversationId', opts.conversationId);
    const wsUrl =
      API_BASE.replace(/^http/, 'ws') + `/api/voice/live?${qs.toString()}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      setError(
        `Failed to open WebSocket: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
      setState('error');
      cleanup();
      return;
    }
    wsRef.current = ws;

    ws.addEventListener('open', () => {
      // Hook worklet → WS once the socket is open.
      node.port.onmessage = (evt: MessageEvent<{
        samples: Float32Array;
        sampleRate: number;
      }>): void => {
        if (ws.readyState !== WebSocket.OPEN) return;
        const { samples, sampleRate } = evt.data;
        const pcm16 = floatToInt16Downsample(
          samples,
          sampleRate,
          INPUT_SAMPLE_RATE,
        );
        if (pcm16.byteLength === 0) return;
        const b64 = uint8ArrayToBase64(new Uint8Array(pcm16.buffer));
        ws.send(
          JSON.stringify({
            type: 'audio',
            data: b64,
            mimeType: `audio/pcm;rate=${INPUT_SAMPLE_RATE}`,
          }),
        );
      };
      source.connect(node);
      // NOTE: we deliberately do NOT connect `node` to `captureCtx.destination`
      // — that would route the user's own mic back to their speakers.
      setState('listening');
    });

    ws.addEventListener('message', (evt: MessageEvent) => {
      let msg: ServerMessage;
      try {
        msg =
          typeof evt.data === 'string'
            ? (JSON.parse(evt.data) as ServerMessage)
            : ({ type: 'error', msg: 'Non-string frame' } as ServerMessage);
      } catch {
        return;
      }
      switch (msg.type) {
        case 'audio':
          scheduleAudio(msg.b64);
          break;
        case 'input_transcript':
          setTranscript((t) => t + msg.text);
          break;
        case 'output_transcript':
          setOutputTranscript((t) => t + msg.text);
          break;
        case 'text':
          // Assistant text part (rare when responseModalities is AUDIO-only,
          // but surface it alongside the output transcript so nothing is lost).
          setOutputTranscript((t) => t + msg.text);
          break;
        case 'tool_call':
          setToolActive(true);
          break;
        case 'turn_complete':
          setToolActive(false);
          break;
        case 'error':
          setError(msg.msg || 'Live session error');
          setState('error');
          cleanup();
          break;
      }
    });

    ws.addEventListener('error', () => {
      setError('WebSocket error');
      setState('error');
      cleanup();
    });

    ws.addEventListener('close', () => {
      setState((s) => (s === 'error' ? 'error' : 'idle'));
      cleanup();
    });
  }, [cleanup, scheduleAudio, state]);

  const sendText = useCallback((text: string): void => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'text', text }));
  }, []);

  useEffect(() => {
    return (): void => cleanup();
  }, [cleanup]);

  return {
    state,
    start,
    stop,
    sendText,
    transcript,
    outputTranscript,
    error,
    toolActive,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────

function base64ToUint8Array(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  // Chunked to avoid `Maximum call stack size exceeded` on large buffers.
  let result = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    result += String.fromCharCode.apply(
      null,
      slice as unknown as number[],
    );
  }
  return btoa(result);
}

/**
 * Nearest-neighbour downsample + float→int16 conversion. No anti-alias
 * filter — acceptable for voice at 48→16 kHz. Returns a newly-allocated
 * Int16Array.
 */
function floatToInt16Downsample(
  floats: Float32Array,
  fromRate: number,
  toRate: number,
): Int16Array {
  if (fromRate === toRate) {
    const out = new Int16Array(floats.length);
    for (let i = 0; i < floats.length; i++) {
      out[i] = floatToInt16Sample(floats[i]);
    }
    return out;
  }
  const ratio = fromRate / toRate;
  const outLen = Math.floor(floats.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcIdx = Math.floor(i * ratio);
    out[i] = floatToInt16Sample(floats[srcIdx] ?? 0);
  }
  return out;
}

function floatToInt16Sample(f: number): number {
  const clamped = Math.max(-1, Math.min(1, f));
  return clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
}
